import { format } from "date-fns";
import React from "react";
import { useParams } from "react-router-dom";

import ComponentLoader from "../../../../components/Spinner/ComponentLoader";

import { AuthorSurvey, SurveyUnavailable } from "./pages";

import { useGetSurveyByLinkId } from "../../../../api/hooks";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlag";

import ErrorPage from "../../../Error";
import { EyeChecklistBrowserPlane } from "../../illustrations/EyeChecklistBrowserPlane";
import { LargeClipboardAvatarsPencil } from "../../illustrations/LargeClipboardAvatarsPencil";

const SurveyAuthoringBase = () => {
	const {
		value: ffSurveys,
		query: { isLoading: isLoadingFFSurveys },
	} = useFeatureFlagQuery("ff-is-surveys-enabled", false);
	const { surveyLinkId = "" } = useParams<{ surveyLinkId: string }>();

	const { data: surveyInfo, isLoading } = useGetSurveyByLinkId(surveyLinkId, {
		// fetch the current version of the survey only once for authoring
		staleTime: Infinity,
		enabled: !isLoadingFFSurveys && ffSurveys,
	});

	if (!ffSurveys && !isLoadingFFSurveys) {
		return <ErrorPage code={403} />;
	}

	if (isLoadingFFSurveys || isLoading) {
		return (
			<span data-testid="component-loader">
				<ComponentLoader />
			</span>
		);
	}

	switch (surveyInfo?.surveyAvailabilityStatus) {
		case "active":
			if (surveyInfo?.survey) {
				return <AuthorSurvey survey={surveyInfo.survey} />;
			}
			return <ErrorPage code={404} />;
		case "scheduled":
			if (surveyInfo?.startDate) {
				return (
					<SurveyUnavailable
						testId="scheduled-survey"
						illustration={<EyeChecklistBrowserPlane />}
						message={`This survey is scheduled to start accepting responses on ${format(new Date(surveyInfo?.startDate), "MMMM dd, yyyy")}.`}
					/>
				);
			} else {
				return (
					<SurveyUnavailable
						testId="scheduled-survey-no-start"
						illustration={<EyeChecklistBrowserPlane />}
						message="This survey hasn't opened yet."
					/>
				);
			}
		case "closed":
			return (
				<SurveyUnavailable
					testId="closed-survey"
					illustration={<EyeChecklistBrowserPlane />}
					message="This survey is closed and no longer accepting responses."
				/>
			);
		case "completed":
			return (
				<SurveyUnavailable
					testId="completed-survey"
					illustration={<LargeClipboardAvatarsPencil />}
					message="Thank you for completing our survey!"
				/>
			);
		case "unavailable":
		default:
			return (
				<SurveyUnavailable
					testId="unavailable-survey"
					illustration={<EyeChecklistBrowserPlane />}
					message="You do not have access to this survey. Please contact your administrator for further inquiries."
				/>
			);
	}
};

export default SurveyAuthoringBase;
